import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import { styled, alpha } from '@mui/material/styles';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { Button, SideDrawer } from './index';

interface ContactVenueDialogProps {
  open: boolean;
  onClose: () => void;
  venueName: string;
  whatsapp?: string | null;
  email?: string | null;
  phone?: string | null;
}

const QuickQuestions = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 24,
});

const QuestionChip = styled(Chip)(({ theme }) => ({
  borderRadius: 20,
  fontFamily: '"IBM Plex Sans", sans-serif',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    borderColor: theme.palette.success.main,
  },
}));

const ContactOptions = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const ContactButton = styled(Button)({
  justifyContent: 'flex-start',
  gap: 12,
  padding: '12px 16px',
});

const WhatsAppButton = styled(ContactButton)({
  backgroundColor: '#25D366',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#20BD5A',
  },
});

const QUICK_QUESTIONS = [
  'Is parking available?',
  'What are the timings?',
  'Do you have equipment for rent?',
  'Is food available?',
  'Can I make a group booking?',
  'Are there any ongoing offers?',
];

export function ContactVenueDialog({
  open,
  onClose,
  venueName,
  whatsapp,
  email,
  phone,
}: ContactVenueDialogProps) {
  const [customMessage, setCustomMessage] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);

  const message = selectedQuestion || customMessage;

  const handleQuestionSelect = (question: string) => {
    setSelectedQuestion(question);
    setCustomMessage(question);
  };

  const formatPhoneForLink = (phone: string) => {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    // Add country code if not present (assuming India)
    return digits.startsWith('91') ? digits : `91${digits}`;
  };

  const handleWhatsApp = () => {
    if (!whatsapp) return;
    const intro = `Hi! I'm interested in ${venueName}.`;
    const fullMessage = message ? `${intro}\n\n${message}` : intro;
    const url = `https://wa.me/${formatPhoneForLink(whatsapp)}?text=${encodeURIComponent(fullMessage)}`;
    window.open(url, '_blank');
    onClose();
  };

  const handleEmail = () => {
    if (!email) return;
    const subject = `Inquiry about ${venueName}`;
    const body =
      message || `Hi, I'm interested in booking at ${venueName}. Please provide more details.`;
    const url = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
    onClose();
  };

  const handlePhone = () => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
    onClose();
  };

  const hasContactInfo = whatsapp || email || phone;

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title="Contact Venue"
      width={480}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      {!hasContactInfo ? (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            Contact information is not available for this venue.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Quick Questions */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Common Questions (tap to select)
          </Typography>
          <QuickQuestions>
            {QUICK_QUESTIONS.map((question) => (
              <QuestionChip
                key={question}
                label={question}
                variant={selectedQuestion === question ? 'filled' : 'outlined'}
                color={selectedQuestion === question ? 'success' : 'default'}
                onClick={() => handleQuestionSelect(question)}
              />
            ))}
          </QuickQuestions>

          {/* Custom Message */}
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Or type your question here..."
            value={customMessage}
            onChange={(e) => {
              setCustomMessage(e.target.value);
              setSelectedQuestion(null);
            }}
            sx={{ mb: 3 }}
          />

          {/* Contact Options */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Choose how to contact
          </Typography>
          <ContactOptions>
            {whatsapp && (
              <WhatsAppButton onClick={handleWhatsApp} variant="primary">
                <WhatsAppIcon />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    WhatsApp
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Get quick response
                  </Typography>
                </Box>
              </WhatsAppButton>
            )}

            {email && (
              <ContactButton onClick={handleEmail} variant="outline">
                <EmailIcon />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Email
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {email}
                  </Typography>
                </Box>
              </ContactButton>
            )}

            {phone && (
              <ContactButton onClick={handlePhone} variant="outline">
                <PhoneIcon />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Call
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {phone}
                  </Typography>
                </Box>
              </ContactButton>
            )}
          </ContactOptions>
        </>
      )}
    </SideDrawer>
  );
}
